/** Words the catalog screens share: kinds, statuses, maturity, freshness, experts and what blocks publishing. */

import { ASSET_KINDS, ASSET_MATURITIES, ASSET_STATUSES, type ExpertOut, type Freshness } from '@/api/types';
import { badgeLabel } from '@/ui/Badge';
import { formatDate } from '@/ui/format';

export const KIND_OPTIONS = ASSET_KINDS.map((k) => ({ value: k, label: badgeLabel('kind', k) }));
export const STATUS_OPTIONS = ASSET_STATUSES.map((s) => ({ value: s, label: badgeLabel('status', s) }));
export const MATURITY_OPTIONS = ASSET_MATURITIES.map((m) => ({ value: m, label: badgeLabel('maturity', m) }));

/** One line for each kind in the create form. */
export const KIND_HINTS: Record<string, string> = {
  platform: 'A product GFT runs or licenses, with components and integrations.',
  accelerator: 'A tool or method that speeds up a delivery, such as a migration.',
  framework: 'A model and principles teams apply, with roles.',
  offer: 'A packaged service or engagement sold to clients.',
};

/** Whole calendar months from `iso` to `now` (never negative), in UTC like the server's six-month rule. */
export function monthsSince(iso: string, now: Date = new Date()): number {
  const then = new Date(iso);
  const months = (now.getUTCFullYear() - then.getUTCFullYear()) * 12 + (now.getUTCMonth() - then.getUTCMonth());
  return Math.max(0, now.getUTCDate() < then.getUTCDate() ? months - 1 : months);
}

/** "Confirmed 3 months ago", "Confirmed this month" or "Not confirmed yet". */
export function confirmedText(lastConfirmedAt: string | null, now: Date = new Date()): string {
  if (!lastConfirmedAt) return 'Not confirmed yet';
  const months = monthsSince(lastConfirmedAt, now);
  if (months === 0) return 'Confirmed this month';
  return `Confirmed ${months} ${months === 1 ? 'month' : 'months'} ago`;
}

export function freshnessValue(freshness: Pick<Freshness, 'stale' | 'last_confirmed_at'>): 'stale' | 'fresh' | 'unconfirmed' {
  if (freshness.stale) return 'stale';
  return freshness.last_confirmed_at ? 'fresh' : 'unconfirmed';
}

/** A provisioned expert who has not signed in, or one whose account is off. */
export function expertNote(expert: Pick<ExpertOut, 'is_active' | 'not_signed_in_until'>): string | null {
  if (!expert.is_active) return 'inactive account';
  if (expert.not_signed_in_until) return `not signed in yet, expires on ${formatDate(expert.not_signed_in_until)}`;
  return null;
}

const MISSING_WORDS: Record<string, string> = {
  expert: 'At least one active expert',
  tagline: 'A tagline',
  summary: 'A summary',
  client_names: 'Shareable text without success-case client names',
};

export function missingText(key: string): string {
  return MISSING_WORDS[key] ?? key;
}

/** The country's English name, or the code when the list does not know it. */
export function countryName(code: string, countries: { code: string; name: string }[] | undefined): string {
  return countries?.find((c) => c.code === code)?.name ?? code;
}
